import type { Dispatch } from "react";
import { createContext, useContext } from "react";

import type {
    ExplicitLLModel,
    ICampaignModalData,
    IRawCompetitor,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

export interface ICampaignEditState {
    isReadOnly: boolean;
    campaignName: string;
    selectedBrands: string[];
    initialDomains: IWebsiteItem[];
    currentDomains: IWebsiteItem[];
    initialCompetitors: IRawCompetitor[];
    currentCompetitors: IRawCompetitor[];
    initialModels: ExplicitLLModel[];
    currentModels: ExplicitLLModel[];
    ownerId: number;
    initialWeeklyReportSubscribed: boolean;
    isWeeklyReportSubscribed: boolean;
    initialIncludeSubdomains: boolean;
    includeSubdomains: boolean;
}

export type CampaignEditAction =
    | { type: "OPEN"; payload: ICampaignEditState }
    | { type: "RESET" }
    | { type: "SET_CAMPAIGN_NAME"; payload: string }
    | { type: "SET_BRANDS"; payload: string[] }
    | { type: "SET_DOMAINS"; payload: IWebsiteItem[] }
    | { type: "SET_COMPETITORS"; payload: IRawCompetitor[] | ((prev: IRawCompetitor[]) => IRawCompetitor[]) }
    | { type: "SET_OWNER_ID"; payload: number }
    | { type: "SET_MODELS"; payload: ExplicitLLModel[] }
    | { type: "SET_WEEKLY_REPORT"; payload: boolean }
    | { type: "SET_INCLUDE_SUBDOMAINS"; payload: boolean };

export const initialEditState: ICampaignEditState = {
    isReadOnly: false,
    campaignName: "",
    selectedBrands: [],
    initialDomains: [],
    currentDomains: [],
    initialCompetitors: [],
    currentCompetitors: [],
    initialModels: [],
    currentModels: [],
    ownerId: -1,
    initialWeeklyReportSubscribed: false,
    isWeeklyReportSubscribed: false,
    initialIncludeSubdomains: false,
    includeSubdomains: false,
};

export const campaignEditReducer = (state: ICampaignEditState, action: CampaignEditAction): ICampaignEditState => {
    switch (action.type) {
        case "OPEN":
            return action.payload;
        case "RESET":
            return initialEditState;
        case "SET_CAMPAIGN_NAME":
            return { ...state, campaignName: action.payload };
        case "SET_BRANDS":
            return { ...state, selectedBrands: action.payload };
        case "SET_DOMAINS":
            return { ...state, currentDomains: action.payload };
        case "SET_COMPETITORS":
            return {
                ...state,
                currentCompetitors:
                    typeof action.payload === "function"
                        ? action.payload(state.currentCompetitors)
                        : action.payload,
            };
        case "SET_OWNER_ID":
            return { ...state, ownerId: action.payload };
        case "SET_MODELS":
            return { ...state, currentModels: action.payload };
        case "SET_WEEKLY_REPORT":
            return { ...state, isWeeklyReportSubscribed: action.payload };
        case "SET_INCLUDE_SUBDOMAINS":
            return { ...state, includeSubdomains: action.payload };
        default:
            return state;
    }
};

export interface ICampaignEditContextValue {
    state: ICampaignEditState;
    dispatch: Dispatch<CampaignEditAction>;
    campaignData: ICampaignModalData;
    hasChanges: boolean;
    isLoading: boolean;
    onApprove: () => void;
    onClose: () => void;
}

export const CampaignEditContext = createContext<ICampaignEditContextValue | null>(null);

export const useCampaignEditContext = (): ICampaignEditContextValue => {
    const context = useContext(CampaignEditContext);
    if (!context) {
        throw new Error("useCampaignEditContext must be used within a CampaignEditContext.Provider");
    }

    return context;
};
